import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import { Icon } from './Icons';
import { resetUnreads } from '../store/slices/chatSlice';
import Lobby from './Lobby';
import RequestList from './WaitingView/RequestList';
import RoomLockToggle from './WaitingView/RoomLockToggle';
import styles from './RoomHeader.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { withRoomContext } from '../lib/RoomClientContext';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material';
import Timer from './Timer';
import { createSelector } from '@reduxjs/toolkit';
import { setShowChat, setShowMembersList } from '../store/slices/layoutSlice';

// Base selectors for user state
const selectClientOptions = (state) => state.user.clientOptions;
const selectRole = (state) => state.user.role;

// Base selectors for lobby state
const selectNumWaiting = (state) => state.lobby.numWaiting;

// Base selectors for chat state
const selectNumUnreads = (state) => state.chat.numUnreads;

// Base selectors for room state
const selectPeerJoined = (state) => state.status.peerJoined;

// Composed selector for RoomHeader
const selectRoomHeaderState = createSelector(
  [
    selectClientOptions,
    selectRole,
    selectNumWaiting,
    selectNumUnreads,
    selectPeerJoined,
  ],
  (clientOptions, role, numWaiting, numUnreads, peerJoined) => ({
    clientOptions,
    role,
    numWaiting,
    numUnreads,
    peerJoined,
  })
);

function RoomHeader({
  roomName,
  startedAt,
  numPeers,
  useLobby,
  hideRoomLock = true,
}) {
  // Use the memoized selector from Redux Toolkit
  const { clientOptions, role, numWaiting, numUnreads, peerJoined } =
    useSelector(selectRoomHeaderState);
  const showMembersList = useSelector((state) => state.layout.showMembersList);
  const showChat = useSelector((state) => state.layout.showChat);

  const [showLobby, setShowLobby] = useState(false);
  const dispatch = useDispatch();

  const onToggleChat = useCallback(() => {
    dispatch(setShowChat(!showChat));
  }, [showChat, dispatch]);

  const onToggleMemberList = useCallback(() => {
    dispatch(setShowMembersList(!showMembersList));
  }, [showMembersList, dispatch]);

  const theme = useTheme();

  useEffect(() => {
    if (showChat) {
      dispatch(resetUnreads());
    }
  }, [numUnreads, showChat, dispatch]);

  return (
    <header className={styles.header}>
      {peerJoined && !clientOptions?.disableMembersList ? (
        <div
          className={classnames(styles.box, styles.small, {
            [styles.hide]: showMembersList,
          })}
        >
          <IconButton
            color="white"
            onClick={onToggleMemberList}
            id="participants-button"
          >
            <Badge
              id="participants-badge"
              badgeContent={Math.max(0, numPeers)}
              color="secondary"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Icon name="participants" />
            </Badge>
          </IconButton>
        </div>
      ) : (
        <div />
      )}
      {roomName ? (
        <div
          style={{ padding: 0, borderRadius: '0.375rem' }}
          className={styles.box}
        >
          <div
            className={classnames(styles.lobbyBox, {
              [styles.clickable]: useLobby,
            })}
            style={{ cursor: useLobby ? 'pointer' : 'default' }}
            onClick={() => {
              setShowLobby(!showLobby);
            }}
            id="room-title"
          >
            {useLobby ? (
              <Badge
                badgeContent={Math.max(0, numWaiting)}
                color="secondary"
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
              >
                <Icon name="cube" width={24} height={24} />
              </Badge>
            ) : null}
            <h2
              style={useLobby ? { marginLeft: '0.4rem' } : {}}
              className={styles.title}
            >
              {roomName}
            </h2>
            {!hideRoomLock && <RoomLockToggle />}
          </div>
          {useLobby && <Lobby show={showLobby} />}
          {!hideRoomLock && <RequestList />}
        </div>
      ) : (
        <div>
          {!hideRoomLock && (
            <>
              <RoomLockToggle />
              <RequestList />
            </>
          )}
        </div>
      )}
      <Stack flexDirection="row" alignItems="center">
        {role !== 'guest' && startedAt ? (
          <Timer
            startedAt={startedAt}
            sx={{
              color: theme.palette.white.main,
              textAlign: 'left',
              minWidth: '60px',
            }}
          />
        ) : (
          <div />
        )}

        {peerJoined && !clientOptions?.disableChat ? (
          <div
            className={classnames(styles.box, styles.small, {
              [styles.hide]: showChat,
            })}
          >
            <IconButton
              color="white"
              id="toggle-chat-button"
              onClick={onToggleChat}
            >
              <Badge
                badgeContent={numUnreads}
                color="secondary"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <Icon name="message" width={28} height={28} />
              </Badge>
            </IconButton>
          </div>
        ) : (
          <div />
        )}
      </Stack>
    </header>
  );
}

// Wrap with room context and add a display name for easier debugging
const WrappedRoomHeader = withRoomContext(React.memo(RoomHeader));
WrappedRoomHeader.displayName = 'RoomHeader';

export default WrappedRoomHeader;
